import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useSettings } from "@/context/SettingsContext";

const ThemeContext = createContext({ theme: "light", toggle: () => {}, canToggle: true, mode: "light_dark" });

function applyTheme(theme) {
  const root = document.documentElement;
  if (theme === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
}

export function ThemeProvider({ children }) {
  const { settings, loading } = useSettings();
  const mode = settings.publicThemeMode || "light_dark";       // light_dark | light_only | dark_only
  const defaultPublic = settings.defaultPublicTheme || "light";

  const [theme, setTheme] = useState(() => {
    try {
      const stored = localStorage.getItem("theme");
      if (stored === "light" || stored === "dark") return stored;
    } catch { /* localStorage unavailable (e.g. private mode) — ignore */ }
    return "light";
  });

  // Resolve effective theme once settings load (handle forced modes + seeded default)
  useEffect(() => {
    if (loading) return;
    let effective = theme;
    let stored = null;
    try { stored = localStorage.getItem("theme"); } catch { /* localStorage unavailable — ignore */ }

    if (mode === "light_only") effective = "light";
    else if (mode === "dark_only") effective = "dark";
    else if (!stored) effective = defaultPublic; // first visit -> seeded default

    setTheme(effective);
    applyTheme(effective);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, mode, defaultPublic]);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const toggle = useCallback(() => {
    setTheme((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      try { localStorage.setItem("theme", next); } catch { /* localStorage unavailable — ignore */ }
      return next;
    });
  }, []);

  const setExplicit = useCallback((t) => {
    try { localStorage.setItem("theme", t); } catch { /* localStorage unavailable — ignore */ }
    setTheme(t);
  }, []);

  const canToggle = mode === "light_dark";

  return (
    <ThemeContext.Provider value={{ theme, toggle, setExplicit, canToggle, mode }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
